import PropTypes from "prop-types";
import { useEffect, useMemo, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import { LuChevronLeft, LuChevronRight } from "react-icons/lu";
import { FaRegCalendar } from "react-icons/fa";
import "react-datepicker/dist/react-datepicker.css";
import styles from "./styles.module.css";

const formatDisplay = (date) => {
  if (!date) return "";
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = date.getFullYear();
  return `${dd}.${mm}.${yyyy}`;
};

const parseDisplayDate = (value) => {
  if (!value) return null;
  const match = value.trim().match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  if (!match) return null;

  const [, dd, mm, yyyy] = match;
  const day = Number(dd);
  const month = Number(mm) - 1;
  const year = Number(yyyy);
  const date = new Date(year, month, day);

  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month ||
    date.getDate() !== day
  ) {
    return null;
  }

  return date;
};

const parseRangeInput = (value) => {
  const parts = value.split(" - ");
  if (parts.length !== 2) return { start: null, end: null, valid: false };

  const start = parseDisplayDate(parts[0]);
  const end = parseDisplayDate(parts[1]);

  if (!start || !end) return { start: null, end: null, valid: false };
  if (start > end) return { start: null, end: null, valid: false };

  return { start, end, valid: true };
};

const DateRangePicker = ({
  startDate: startProp,
  endDate: endProp,
  onApply,
  getMaxDate,
  isRangeAllowed,
  onInvalidRange,
}) => {
  const wrapperRef = useRef(null);

  const [isOpen, setIsOpen] = useState(false);
  const [startDateState, setStartDateState] = useState(startProp);
  const [endDateState, setEndDateState] = useState(endProp);
  const [inputText, setInputText] = useState(
    startProp && endProp ? `${formatDisplay(startProp)} - ${formatDisplay(endProp)}` : ""
  );

  useEffect(() => {
    setStartDateState(startProp);
    setEndDateState(endProp);
    setInputText(
      startProp && endProp ? `${formatDisplay(startProp)} - ${formatDisplay(endProp)}` : ""
    );
  }, [startProp, endProp]);

  const currentText = useMemo(() => {
    if (!startDateState || !endDateState) return "";
    return `${formatDisplay(startDateState)} - ${formatDisplay(endDateState)}`;
  }, [startDateState, endDateState]);

  const effectiveMaxDate = useMemo(
    () => (getMaxDate ? getMaxDate(startDateState) : null),
    [startDateState, getMaxDate]
  );

  const allowRange = (start, end) => {
    if (!(start && end)) return false;
    if (isRangeAllowed) return !!isRangeAllowed(start, end);
    return true;
  };

  const revertInvalid = (start, end) => {
    if (onInvalidRange) {
      onInvalidRange(start, end); // parent can reset to initial values
      return;
    }
    setInputText(currentText);
  };

  const openPopover = () => {
    setIsOpen(true);
  };

  const closePopover = () => {
    setStartDateState(startProp);
    setEndDateState(endProp);
    setIsOpen(false);
  };

  const commitRange = (start, end, close = true) => {
    if (!(start && end)) return;
    setStartDateState(start);
    setEndDateState(end);
    setInputText(`${formatDisplay(start)} - ${formatDisplay(end)}`);
    onApply(start, end);
    if (close) setIsOpen(false);
  };

  const handleCalendarChange = (dates) => {
    const [start, end] = dates;

    // partial selection while picking range
    setStartDateState(start);
    setEndDateState(end);

    if (start && end) {
      if (allowRange(start, end)) {
        commitRange(start, end, true);
      } else {
        revertInvalid(start, end);
      }
    }
  };

  useEffect(() => {
    const onDocMouseDown = (e) => {
      if (!isOpen) return;
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        closePopover();
      }
    };

    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [isOpen]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputText(value);

    const parsed = parseRangeInput(value);
    if (parsed.valid) {
      if (allowRange(parsed.start, parsed.end)) {
        commitRange(parsed.start, parsed.end, false);
      } else {
        revertInvalid(parsed.start, parsed.end);
      }
    }
  };

  const handleInputBlur = () => {
    if (!inputText.trim()) {
      setStartDateState(null);
      setEndDateState(null);
      onApply(null, null);
      return;
    }

    const parsed = parseRangeInput(inputText);
    if (parsed.valid) {
      if (allowRange(parsed.start, parsed.end)) {
        commitRange(parsed.start, parsed.end, false);
      } else {
        revertInvalid(parsed.start, parsed.end);
      }
    } else {
      setInputText(currentText);
    }
  };

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      <div className={styles.inputWrap}>
        <input
          type="text"
          className={styles.inputField}
          value={inputText}
          placeholder="DD.MM.YYYY - DD.MM.YYYY"
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onClick={openPopover}
          onFocus={openPopover}
        />
        <button
          type="button"
          className={styles.iconBtn}
          aria-label="Open date range picker"
          onClick={openPopover}
        >
          <FaRegCalendar className={styles.icon} size={16} />
        </button>
      </div>

      {isOpen && (
        <div className={styles.popover}>
          <DatePicker
            inline
            selected={startDateState}
            onChange={handleCalendarChange}
            startDate={startDateState}
            endDate={endDateState}
            selectsRange
            monthsShown={2}
            maxDate={effectiveMaxDate || undefined}
            shouldCloseOnSelect={false}
            renderCustomHeader={({ monthDate, customHeaderCount, decreaseMonth, increaseMonth }) => (
              <div className={styles.header}>
                {customHeaderCount === 0 ? (
                  <button type="button" className={styles.navBtn} onClick={decreaseMonth}>
                    <LuChevronLeft size={14} />
                  </button>
                ) : (
                  <span className={styles.navBtnPlaceholder} />
                )}

                <span className={styles.monthLabel}>
                  {monthDate.toLocaleString("en-US", { month: "long", year: "numeric" })}
                </span>

                {customHeaderCount === 1 ? (
                  <button type="button" className={styles.navBtn} onClick={increaseMonth}>
                    <LuChevronRight size={14} />
                  </button>
                ) : (
                  <span className={styles.navBtnPlaceholder} />
                )}
              </div>
            )}
          />
        </div>
      )}
    </div>
  );
};

DateRangePicker.propTypes = {
  startDate: PropTypes.instanceOf(Date),
  endDate: PropTypes.instanceOf(Date),
  onApply: PropTypes.func,
  getMaxDate: PropTypes.func,
  isRangeAllowed: PropTypes.func,
  onInvalidRange: PropTypes.func,
};

DateRangePicker.defaultProps = {
  startDate: null,
  endDate: null,
  onApply: () => {},
  getMaxDate: null,
  isRangeAllowed: null,
  onInvalidRange: null,
};

export default DateRangePicker;